import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import { ArrowUpRight, ArrowDownRight, Wallet, CalendarClock, AlertTriangle, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/i18n";

function ChartTip({ active, payload, label, fmt, monthLabel }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 shadow-lg">
      <p className="font-mono text-[10px] uppercase tracking-widest text-slate-400">{monthLabel(label)}</p>
      {payload.map((p) => (
        <p key={p.dataKey} className="tnum mt-1 text-xs font-medium" style={{ color: p.stroke || p.color }}>
          {p.name}: {fmt(p.value)}
        </p>
      ))}
    </div>
  );
}

export default function Dashboard() {
  const { t, fmt, fmtDate, monthLabel } = useI18n();
  const [d, setD] = useState(null);
  const [fxHist, setFxHist] = useState([]);

  const load = () => api.get("/dashboard").then((r) => setD(r.data));
  useEffect(() => {
    load();
    api.get("/fx/history").then((r) => setFxHist(r.data || [])).catch(() => {});
  }, []);

  if (!d) return <p className="py-24 text-center text-sm text-slate-400" data-testid="dashboard-loading">{t.app.loading}</p>;

  const today = new Date().toISOString().slice(0, 10);
  const kpis = [
    { id: "total-balance", label: t.dash.totalBalance, value: fmt(d.total_balance), cls: "text-slate-900", sub: d.fx_rates?.USD ? `${d.banks.length} ${t.dash.accounts.toLowerCase()} · 1$≈₺${d.fx_rates.USD.toFixed(2)} · 1€≈₺${d.fx_rates.EUR.toFixed(2)}` : `${d.banks.length} ${t.dash.accounts.toLowerCase()}` },
    { id: "month-income", label: t.dash.monthIncome, value: fmt(d.month_income), cls: "text-positive", icon: <ArrowUpRight className="h-3.5 w-3.5" /> },
    { id: "month-expense", label: t.dash.monthExpense, value: fmt(d.month_expense), cls: "text-negative", icon: <ArrowDownRight className="h-3.5 w-3.5" /> },
    { id: "month-net", label: t.dash.monthNet, value: fmt(d.month_net), cls: d.month_net >= 0 ? "text-positive" : "text-negative" },
    { id: "pending-receivables", label: t.dash.pendingReceivables, value: fmt(d.pending_receivables), cls: "text-slate-900", sub: `${d.overdue_receivables} ${t.dash.overdueRecs}`, warn: d.overdue_receivables > 0 },
  ];

  const series = d.series.map((s) => ({ ...s, label: s.month }));
  const lastFx = fxHist[fxHist.length - 1];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5">
        {kpis.map((k) => (
          <div key={k.id} data-testid={`kpi-${k.id}-card`}
            className="rounded-2xl border border-slate-200/70 bg-white p-5 shadow-sm transition-transform duration-200 hover:-translate-y-0.5">
            <p className="text-[11px] font-medium uppercase tracking-wider text-slate-400">{k.label}</p>
            <p className={`tnum mt-2 font-display text-xl font-semibold tracking-tight sm:text-2xl ${k.cls}`}>{k.value}</p>
            {k.sub && (
              <p className={`mt-1.5 font-mono text-[10px] ${k.warn ? "text-red-500" : "text-slate-400"}`}>{k.sub}</p>
            )}
          </div>
        ))}
      </div>

      {(() => {
        const over = (d.budgets || []).filter((b) => b.over);
        if (!over.length) return null;
        return (
          <Link to="/app/butceler" data-testid="budget-warning-banner"
            className="flex items-center gap-3 rounded-2xl border border-red-200 bg-red-50 px-5 py-4 transition-colors hover:bg-red-100/70">
            <AlertTriangle className="h-5 w-5 shrink-0 text-red-600" />
            <p className="text-sm text-red-700">
              <span className="font-semibold">{t.budget.overBudget}:</span> {over.map((b) => b.category).join(", ")}
            </p>
            <ArrowRight className="ml-auto h-4 w-4 text-red-400" />
          </Link>
        );
      })()}

      <div className="grid gap-6 xl:grid-cols-3">
        <div className="rounded-2xl border border-slate-200/70 bg-white p-6 shadow-sm xl:col-span-2" data-testid="cashflow-chart">
          <p className="font-display text-sm font-semibold text-slate-900">{t.dash.cashflow}</p>
          <div className="mt-5 h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={series} margin={{ top: 5, right: 5, left: -12, bottom: 0 }}>
                <defs>
                  <linearGradient id="gInc" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0055FF" stopOpacity={0.18} />
                    <stop offset="100%" stopColor="#0055FF" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gExp" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#DC2626" stopOpacity={0.12} />
                    <stop offset="100%" stopColor="#DC2626" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 5" stroke="#0F172A" strokeOpacity={0.07} vertical={false} />
                <XAxis dataKey="label" tickFormatter={monthLabel} tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTip fmt={fmt} monthLabel={monthLabel} />} cursor={{ stroke: "#94A3B8", strokeDasharray: "3 3" }} />
                <Area type="monotone" dataKey="income" name={t.app.income} stroke="#0055FF" strokeWidth={2} fill="url(#gInc)" />
                <Area type="monotone" dataKey="expense" name={t.app.expense} stroke="#DC2626" strokeWidth={1.5} strokeDasharray="5 4" fill="url(#gExp)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200/70 bg-white p-6 shadow-sm" data-testid="upcoming-payments-list">
          <div className="flex items-center justify-between">
            <p className="font-display text-sm font-semibold text-slate-900">{t.dash.upcoming}</p>
            <Link to="/app/odemeler" className="font-mono text-[10px] uppercase tracking-widest text-brand hover:underline">{t.dash.viewAll}</Link>
          </div>
          <div className="mt-4 space-y-2.5">
            {d.upcoming_payments.length === 0 && <p className="py-8 text-center text-sm text-slate-400">{t.app.noData}</p>}
            {d.upcoming_payments.map((p) => {
              const late = p.due_date < today;
              return (
                <div key={p.id} className="flex items-center gap-3 rounded-xl border border-slate-100 px-4 py-3">
                  <CalendarClock className={`h-4 w-4 shrink-0 ${late ? "text-red-500" : "text-slate-400"}`} />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-slate-900">{p.title}</p>
                    <p className="font-mono text-[10px] text-slate-400">{fmtDate(p.due_date)}{p.cari_name ? ` · ${p.cari_name}` : ""}</p>
                  </div>
                  <p className="tnum ml-auto shrink-0 font-mono text-sm font-medium text-slate-900">{fmt(p.amount)}</p>
                  {late && <span className="shrink-0 rounded-full bg-red-50 px-2 py-0.5 font-mono text-[9px] uppercase text-red-600">{t.app.overdue}</span>}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className={`grid gap-6 ${fxHist.length ? "xl:grid-cols-4" : "xl:grid-cols-3"}`}>
        <div className="rounded-2xl border border-slate-200/70 bg-white p-6 shadow-sm xl:col-span-2" data-testid="recent-transactions-table">
          <div className="flex items-center justify-between">
            <p className="font-display text-sm font-semibold text-slate-900">{t.dash.recent}</p>
            <Link to="/app/islemler" className="font-mono text-[10px] uppercase tracking-widest text-brand hover:underline">{t.dash.viewAll}</Link>
          </div>
          <div className="mt-4 divide-y divide-slate-100">
            {d.recent_transactions.map((x) => (
              <div key={x.id} className="flex items-center gap-3 py-2.5">
                <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${x.type === "income" ? "bg-positive" : "bg-negative"}`} />
                <div className="min-w-0">
                  <p className="truncate text-sm text-slate-800">{x.description || x.category}</p>
                  <p className="font-mono text-[10px] text-slate-400">{fmtDate(x.date)} · {x.category}</p>
                </div>
                <p className={`tnum ml-auto shrink-0 font-mono text-sm font-medium ${x.type === "income" ? "text-positive" : "text-negative"}`}>
                  {x.type === "income" ? "+" : "−"}{fmt(x.amount)}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200/70 bg-white p-6 shadow-sm" data-testid="dashboard-banks-list">
          <p className="font-display text-sm font-semibold text-slate-900">{t.dash.accounts}</p>
          <div className="mt-4 space-y-2.5">
            {d.banks.map((b) => (
              <div key={b.id} className="flex items-center gap-3 rounded-xl border border-slate-100 px-4 py-3">
                <Wallet className="h-4 w-4 shrink-0 text-slate-400" />
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-900">{b.name}</p>
                  <p className="font-mono text-[10px] text-slate-400">{b.bank_name}</p>
                </div>
                <p className="tnum ml-auto shrink-0 font-mono text-sm font-medium text-slate-900">{fmt(b.balance, b.currency)}</p>
              </div>
            ))}
          </div>
        </div>

        {fxHist.length > 0 && (
          <div className="rounded-2xl border border-slate-200/70 bg-white p-6 shadow-sm" data-testid="fx-trend-chart">
            <p className="font-display text-sm font-semibold text-slate-900">{t.dashFx.trend}</p>
            <div className="mt-4 h-40">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={fxHist} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 5" stroke="#0F172A" strokeOpacity={0.07} vertical={false} />
                  <XAxis dataKey="date" tickFormatter={(v) => v.slice(5).split("-").reverse().join(".")}
                    tick={{ fontSize: 9, fill: "#94A3B8" }} axisLine={false} tickLine={false} minTickGap={28} />
                  <YAxis domain={["auto", "auto"]} tickFormatter={(v) => `₺${v.toFixed(0)}`}
                    tick={{ fontSize: 9, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                  <Tooltip
                    formatter={(v, name) => [`₺${Number(v).toFixed(2)}`, name === "usd" ? "USD/TRY" : "EUR/TRY"]}
                    contentStyle={{ border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 11, boxShadow: "0 8px 24px rgba(15,23,42,0.08)" }} />
                  <Line type="monotone" dataKey="usd" stroke="#0055FF" strokeWidth={1.75} dot={false} />
                  <Line type="monotone" dataKey="eur" stroke="#0F172A" strokeWidth={1.5} strokeDasharray="4 3" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-3 flex gap-4 font-mono text-[10px] text-slate-400" data-testid="fx-latest-values">
              <span className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-brand" />USD/TRY ₺{lastFx?.usd?.toFixed(2)}</span>
              <span className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-slate-900" />EUR/TRY ₺{lastFx?.eur?.toFixed(2)}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
